'use client';

import { useCart } from '@/features/checkout/CartProvider';
import { clientFetch } from '@/lib/api/clientFetch';
import { useMemo, useState, useTransition } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { PatchCheck } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import './couponInput.scss';

function readMessage(messages, key, fallback = '') {
  const resolved = String(key || '')
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement ?? '')),
    String(template || ''),
  );
}

function stripTags(value = '') {
  return String(value).replace(/<[^>]+>/g, '');
}

function getCheckoutLocale(currency = 'USD') {
  switch (currency) {
    case 'XOF':
      return 'fr-SN';
    case 'EUR':
      return 'fr';
    case 'USD':
    default:
      return 'en';
  }
}

function hasFreeShippingBenefit(appliedBenefits = []) {
  return Array.isArray(appliedBenefits)
    ? appliedBenefits.some((benefit) => benefit?.type === 'free_shipping')
    : false;
}

function getCouponItems(cartItems = []) {
  return (Array.isArray(cartItems) ? cartItems : [])
    .filter((item) => !item?.isGift)
    .map((item) => ({
      productNumber: item.productNumber,
      qty: item.qty,
    }));
}

export default function CouponCard({
  title = 'Coupon',
  className = '',
  compact = false,
  messages = {},
}) {
  const { appliedBenefits, applyDiscount, cartItems, clearDiscount, discount } =
    useCart();
  const [couponCode, setCouponCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isPending, startTransition] = useTransition();

  const purchasableItems = useMemo(
    () => getCouponItems(cartItems),
    [cartItems],
  );
  const checkoutCurrency = cartItems[0]?.currency || 'USD';
  const checkoutLocale = getCheckoutLocale(checkoutCurrency);
  const hasCoupon = Boolean(discount?.code);
  const hasDiscountAmount = Number(discount?.amount || 0) > 0;
  const hasFreeShipping = hasFreeShippingBenefit(appliedBenefits);

  const handleApplyCoupon = async (event) => {
    event.preventDefault();
    const nextCode = couponCode.trim().toUpperCase();

    setErrorMessage('');

    if (!nextCode) {
      const message = readMessage(
        messages,
        'emptyError',
        'Enter a coupon code.',
      );
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    if (!purchasableItems.length) {
      const message = 'Add an item to your cart before applying a coupon.';
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    try {
      const response = await clientFetch('/api/coupons/apply', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-lang': checkoutLocale,
          'accept-language': checkoutLocale,
        },
        body: JSON.stringify({
          code: nextCode,
          items: purchasableItems,
        }),
      });

      applyDiscount({
        code: response.code || nextCode,
        amount: Number(response.amount || 0),
        description: response.description || '',
        label: response.label || '',
        appliedBenefits: Array.isArray(response.appliedBenefits)
          ? response.appliedBenefits
          : Array.isArray(response.benefits)
            ? response.benefits
            : [],
        giftItems: Array.isArray(response.giftItems) ? response.giftItems : [],
      });

      setCouponCode('');
      toast.success(
        response.description ||
          readMessage(messages, 'appliedGeneric', 'Coupon applied.'),
      );
    } catch (error) {
      const serverMessage = error?.data?.message || '';
      const mappedMessage = {
        'Coupon code is required': readMessage(
          messages,
          'messages.codeRequired',
          'Coupon code is required',
        ),
        'Coupon not found': readMessage(
          messages,
          'messages.notFound',
          'Coupon not found',
        ),
        'Coupon is not active': readMessage(
          messages,
          'messages.notActive',
          'Coupon is not active',
        ),
        'Coupon is not yet valid': readMessage(
          messages,
          'messages.notYetValid',
          'Coupon is not yet valid',
        ),
        'Coupon has expired': readMessage(
          messages,
          'messages.expired',
          'Coupon has expired',
        ),
        'Coupon usage limit reached': readMessage(
          messages,
          'messages.usageLimitReached',
          'Coupon usage limit reached',
        ),
        'You have already used this coupon': readMessage(
          messages,
          'messages.alreadyUsed',
          'You have already used this coupon',
        ),
        'Coupon does not apply to any items in the cart': readMessage(
          messages,
          'messages.notApplicable',
          'Coupon does not apply to items in your cart',
        ),
        'Server error applying coupon': readMessage(
          messages,
          'invalid',
          'Invalid or expired coupon',
        ),
      };
      const message =
        mappedMessage[serverMessage] ||
        error?.message ||
        readMessage(messages, 'invalid', 'Unable to apply coupon.');
      setErrorMessage(message);
      toast.error(message);
    }
  };

  const handleRemoveCoupon = () => {
    startTransition(() => {
      clearDiscount();
      setCouponCode('');
      setErrorMessage('');
      toast.info(readMessage(messages, 'removed', 'Coupon removed.'));
    });
  };

  return (
    <>
      {!hasCoupon ? (
        <>
          <Form
            className={`coupon-card__form coupon-input-form ${className} ${compact ? 'd-flex gap-2' : ''}`.trim()}
            onSubmit={handleApplyCoupon}
          >
            <InputGroup>
              <Form.Control
                type="text"
                name="coupon-input"
                value={couponCode}
                onChange={(event) => setCouponCode(event.target.value)}
                placeholder={readMessage(
                  messages,
                  'placeholder',
                  'Enter coupon code',
                )}
                autoComplete="off"
                disabled={isPending}
                className={`rounded-start-pill shadow ${compact ? 'form-control-sm' : ''}`.trim()}
                data-coupon-input
              />
              <Button 
                type="submit"
                variant="transparent"
                disabled={isPending}
                size={compact ? 'sm' : undefined}
                className={`coupon-input-btn app-button app-button--primary ${compact ? 'px-2 fs-8 gap-1' : 'px-3'}`.trim()}
              >
                <PatchCheck size={compact ? 18 : 24} />
                <span>
                  {isPending
                    ? readMessage(messages, 'loading', 'Applying...')
                    : readMessage(messages, 'apply', 'Apply')}
                </span>
              </Button>
            </InputGroup>
          </Form>

          {errorMessage ? (
            <div className="auth-message auth-message--error">
              {errorMessage}
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}
